import { useState, useEffect, useRef } from 'react';
import { createClient, createClientWithPlan } from '../../services/clientService';
import { getProfileByEmail } from '../../services/profileService';
import { searchAIAgents } from '../../services/aiAgentService';
import type { RetellAIAgent } from '../../types/aiAgent';
import type { ProfileByEmailResponse } from '../../types/profile';

export interface OnboardPrefill {
  email: string;
  displayName: string;
  clientName: string;
  teamName: string;
  totalSeats: number;
  mysteryShopPlan: string | null;
  shopsMonthly: number | null;
  agentId: string | null;
  demo: boolean;
  profileId: string | null;
  businessId: string | null;
  clientId: string | null;
  invitationAccepted?: boolean;
}

interface AddClientModalProps {
  onClose: () => void;
  onSuccess: () => void;
  prefill?: OnboardPrefill;
}

const SHOP_PLANS = [
  { value: 'basic', label: 'One-Time', defaultShops: 1, editable: true },
  { value: 'starter', label: 'Starter', defaultShops: 10, editable: true },
  { value: 'pro', label: 'Pro', defaultShops: 25, editable: true },
  { value: 'custom', label: 'Custom', defaultShops: null, editable: true },
] as const;

export function AddClientModal({ onClose, onSuccess, prefill }: AddClientModalProps) {
  const [segment, setSegment] = useState<'onboard' | 'add-only'>(prefill ? 'onboard' : 'onboard');

  // Add Client Only state
  const [name, setName] = useState('');
  const [addOnlyDemo, setAddOnlyDemo] = useState(import.meta.env.VITE_ENV === 'dev');

  // Onboard - email lookup
  const [email, setEmail] = useState(prefill?.email ?? '');
  const [emailLookupDone, setEmailLookupDone] = useState(!!prefill);
  const [existingProfile, setExistingProfile] = useState<ProfileByEmailResponse | null>(null);
  const [lookingUp, setLookingUp] = useState(false);

  // Onboard - form fields
  const [clientName, setClientName] = useState(prefill?.clientName ?? '');
  const [displayName, setDisplayName] = useState(prefill?.displayName ?? '');
  const [teamName, setTeamName] = useState(prefill?.teamName ?? '');
  const teamNameTouched = useRef(!!prefill);
  const [totalSeats, setTotalSeats] = useState(prefill?.totalSeats ?? 0);
  const [shopEnabled, setShopEnabled] = useState(!!prefill?.mysteryShopPlan);
  const [shopPlan, setShopPlan] = useState(prefill?.mysteryShopPlan ?? '');
  const [shopsMonthly, setShopsMonthly] = useState<number | null>(prefill?.shopsMonthly ?? null);
  const [demo, setDemo] = useState(prefill?.demo ?? import.meta.env.VITE_ENV === 'dev');
  const [retellAgentId, setRetellAgentId] = useState(prefill?.agentId ?? '');

  // Onboard - profile IDs from lookup
  const [profileId, setProfileId] = useState<string | null>(prefill?.profileId ?? null);
  const [businessId, setBusinessId] = useState<string | null>(prefill?.businessId ?? null);
  const [clientId, setClientId] = useState<string | null>(prefill?.clientId ?? null);

  // Agents
  const [agents, setAgents] = useState<RetellAIAgent[]>([]);
  const [fetching, setFetching] = useState(true);

  // Shared state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Success state
  const [successLink, setSuccessLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Fetch agents on mount
  useEffect(() => {
    searchAIAgents({ active: true }, { pageSize: 1000, orderBy: 'name', order: 'asc' })
      .then((ags) => {
        setAgents(ags);
        if (ags.length === 1) setRetellAgentId(ags[0].retellAgentId);
      })
      .catch(() => setError('Failed to load agents'))
      .finally(() => setFetching(false));
  }, []);

  // Auto-sync teamName from clientName when untouched
  const handleClientNameChange = (value: string) => {
    setClientName(value);
    if (!teamNameTouched.current) {
      setTeamName(value);
    }
  };

  const handleTeamNameChange = (value: string) => {
    teamNameTouched.current = true;
    setTeamName(value);
  };

  // Email lookup
  const handleContinue = async () => {
    if (!email.trim()) return;
    setLookingUp(true);
    setError('');
    try {
      const profile = await getProfileByEmail(email.trim());
      setEmailLookupDone(true);
      if (profile) {
        setExistingProfile(profile);
        setDisplayName(profile.displayName || '');
        if (profile.business?.client?.name) {
          setClientName(profile.business.client.name);
          if (!teamNameTouched.current) setTeamName(profile.business.client.name);
        }
        if (profile.business?.activePlan) {
          setTotalSeats(profile.business.activePlan.totalSeats || 0);
          if (profile.business.activePlan.mysteryShopPlan) {
            const plan = profile.business.activePlan.mysteryShopPlan;
            setShopEnabled(true);
            setShopPlan(plan);
            const planDef = SHOP_PLANS.find((p) => p.value === plan);
            setShopsMonthly(profile.business.activePlan.shopsMonthly ?? planDef?.defaultShops ?? null);
          }
        }
        if (profile.business?.activePlan?.agentId) {
            setRetellAgentId(profile.business.activePlan.agentId);
          }
        if (profile.business?.demo) setDemo(true);
        setProfileId(profile.id || null);
        setBusinessId(profile.businessId || null);
        setClientId(profile.business?.clientId || null);
      }
    } catch {
      setError('Failed to look up email');
    } finally {
      setLookingUp(false);
    }
  };

  // Shop plan change handler
  const handleShopPlanChange = (value: string) => {
    setShopPlan(value);
    const plan = SHOP_PLANS.find((p) => p.value === value);
    if (plan) {
      setShopsMonthly(plan.defaultShops);
    }
  };

  const selectedShopPlan = SHOP_PLANS.find((p) => p.value === shopPlan);

  // Add Client Only submit
  const handleAddOnlySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setError('');
    setLoading(true);
    try {
      await createClient({ name: name.trim(), demo: addOnlyDemo });
      onSuccess();
    } catch {
      setError('Failed to create client');
    } finally {
      setLoading(false);
    }
  };

  // Onboard validation
  const canSubmitOnboard =
    clientName.trim() &&
    email.trim() &&
    displayName.trim() &&
    teamName.trim() &&
    retellAgentId &&
    emailLookupDone &&
    (!shopEnabled || shopPlan);

  // Onboard submit
  const handleOnboardSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmitOnboard) return;
    setError('');
    setLoading(true);
    try {
      const result = await createClientWithPlan({
        email: email.trim(),
        displayName: displayName.trim(),
        clientName: clientName.trim(),
        teamName: teamName.trim(),
        totalSeats: totalSeats || null,
        shopPlan: shopEnabled ? shopPlan : null,
        shopsMonthly: shopEnabled && shopPlan !== 'custom' ? shopsMonthly : null,
        demo,
        clientId,
        businessId,
        profileId,
        agentId: retellAgentId,
      });
      setSuccessLink(result?.stripeRedirectLink ?? result?.activePlan?.stripeRedirectLink ?? '');
    } catch {
      setError('Failed to onboard client');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        {segment === 'onboard' && successLink !== null ? (
          // Success view
          <div className="px-6 pt-6 pb-4">
            <h2 className="text-lg font-bold text-gray-900 mb-4">{prefill?.invitationAccepted ? 'Plan Updated' : 'Client Onboarded'}</h2>
            {(!prefill || prefill.invitationAccepted) && successLink && (
              <div>
                <label className={labelClass}>Stripe Redirect Link</label>
                <div className="flex items-center gap-2">
                  <input type="text" readOnly value={successLink} className={inputClass} />
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(successLink);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="shrink-0 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            )}
            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={() => { onSuccess(); }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={segment === 'add-only' ? handleAddOnlySubmit : handleOnboardSubmit}
            className="flex flex-col max-h-[90vh]"
          >
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              {prefill ? (
                <h2 className="text-lg font-bold text-gray-900 mb-4">Edit Plan</h2>
              ) : (
                /* Segment Toggle */
                <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden mb-4">
                  <button
                    type="button"
                    onClick={() => setSegment('onboard')}
                    className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                      segment === 'onboard'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Onboard Client
                  </button>
                  <button
                    type="button"
                    onClick={() => setSegment('add-only')}
                    className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                      segment === 'add-only'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Add Client Only
                  </button>
                </div>
              )}

              {segment === 'add-only' ? (
                // Existing simple form
                <div className="space-y-4">
                  <div>
                    <label htmlFor="client-name" className={labelClass}>Name *</label>
                    <input
                      id="client-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      autoFocus
                      className={inputClass}
                      placeholder="Client name"
                    />
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={addOnlyDemo}
                        onChange={(e) => setAddOnlyDemo(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      Demo (REV Demo Client)
                    </label>
                  </div>
                </div>
              ) : fetching ? (
                <p className="text-sm text-gray-400">Loading...</p>
              ) : (
                // Onboard form
                <div className="space-y-4">
                  {/* Identity fields — hidden after invitation accepted in edit mode */}
                  {!(prefill?.invitationAccepted) && (
                    <>
                      {/* Owner Email + Continue/Status */}
                      {(() => {
                        const emailInvalid = email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
                        return (
                          <>
                            <div>
                              <label className={labelClass}>Owner Email *</label>
                              <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={emailLookupDone}
                                autoFocus
                                className={`${inputClass}${emailLookupDone ? ' bg-gray-100' : ''}${emailInvalid ? ' border-red-400' : ''}`}
                                placeholder="email@example.com"
                              />
                              {emailInvalid && (
                                <p className="text-red-500 text-xs mt-1">Enter a valid email (e.g. user@example.com)</p>
                              )}
                            </div>

                            {!emailLookupDone && (
                              <button
                                type="button"
                                onClick={handleContinue}
                                disabled={lookingUp || !email.trim() || emailInvalid}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {lookingUp ? 'Looking up...' : 'Continue'}
                              </button>
                            )}
                          </>
                        );
                      })()}

                      {emailLookupDone && existingProfile && (
                        <p className="text-sm font-medium text-red-600">Existing Profile</p>
                      )}
                    </>
                  )}

                  {/* Rest of form - only after email lookup (or immediately if invitationAccepted) */}
                  {(emailLookupDone || prefill?.invitationAccepted) && (
                    <>
                      {!(prefill?.invitationAccepted) && (
                        <>
                          <div>
                            <label className={labelClass}>Client Name *</label>
                            <input
                              type="text"
                              value={clientName}
                              onChange={(e) => handleClientNameChange(e.target.value)}
                              required
                              className={inputClass}
                              placeholder="Client name"
                            />
                          </div>

                          <div>
                            <label className={labelClass}>Owner Display Name *</label>
                            <input
                              type="text"
                              value={displayName}
                              onChange={(e) => setDisplayName(e.target.value)}
                              required
                              className={inputClass}
                              placeholder="Display name"
                            />
                          </div>

                          <div>
                            <label className={labelClass}>Team Name *</label>
                            <input
                              type="text"
                              value={teamName}
                              onChange={(e) => handleTeamNameChange(e.target.value)}
                              required
                              className={inputClass}
                              placeholder="Team name"
                            />
                          </div>
                        </>
                      )}

                      {/* LMS */}
                      <div>
                        <label className={labelClass}>Starting Seats</label>
                        <input
                          type="number"
                          value={totalSeats}
                          onChange={(e) => setTotalSeats(Number(e.target.value))}
                          min={0}
                          className={inputClass}
                        />
                      </div>

                      {/* Mystery Shop */}
                      <div>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={shopEnabled}
                            onChange={(e) => {
                              setShopEnabled(e.target.checked);
                              if (!e.target.checked) {
                                setShopPlan('');
                                setShopsMonthly(null);
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                          Mystery Shop
                        </label>
                      </div>

                      {shopEnabled && (
                        <div className="flex items-start gap-3">
                          <div className="flex-1">
                            <label className={labelClass}>Plan *</label>
                            <select
                              value={shopPlan}
                              onChange={(e) => handleShopPlanChange(e.target.value)}
                              required
                              className={inputClass}
                            >
                              <option value="">Select a plan...</option>
                              {SHOP_PLANS.map((p) => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                              ))}
                            </select>
                          </div>
                          {selectedShopPlan && (
                            <div className="w-28">
                              <label className={labelClass}>Shops/mo</label>
                              <input
                                type="number"
                                value={shopsMonthly ?? ''}
                                onChange={(e) => setShopsMonthly(Number(e.target.value))}
                                min={1}
                                disabled={!selectedShopPlan.editable}
                                className={`${inputClass}${!selectedShopPlan.editable ? ' bg-gray-100' : ''}`}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {/* AI Agent */}
                      <div>
                        <label className={labelClass}>AI Agent *</label>
                        <select
                          value={retellAgentId}
                          onChange={(e) => setRetellAgentId(e.target.value)}
                          required
                          className={inputClass}
                        >
                          <option value="">Select agent</option>
                          {agents.map((a) => (
                            <option key={a.retellAgentId} value={a.retellAgentId}>{a.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Demo - only for new clients, not edit */}
                      {!prefill && (
                        <div>
                          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={demo}
                              onChange={(e) => setDemo(e.target.checked)}
                              className="rounded border-gray-300"
                            />
                            Demo (REV Demo Client)
                          </label>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              {segment === 'add-only' ? (
                <button
                  type="submit"
                  disabled={loading || !name.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Adding...' : 'Add Client'}
                </button>
              ) : emailLookupDone ? (
                <button
                  type="submit"
                  disabled={loading || !canSubmitOnboard}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (prefill ? 'Saving...' : 'Onboarding...') : (prefill ? 'Save' : 'Onboard Client')}
                </button>
              ) : null}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
